import React, { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { lighten, adjustHue, darken } from 'polished'
import heroReel from '../../video/hero-reel.mp4'
import heroPoster from '../../video/hero-reel-poster.jpg'

// Seconds into hero-reel.mp4. The first segment is the zoom into the mosaic
// and plays once; the rest rotate in a fresh shuffle each cycle. Every start is
// a keyframe, so seeking to one is instant.
const INTRO = [0, 8]
const INTRO_SETTLE = 6 // seconds; the zoom lands at the same moment
const CLIPS = [[8, 14], [14, 20], [20, 26], [26, 34], [34, 40], [40, 48], [48, 54], [54, 60], [60, 66], [66, 72], [72, 78], [78, 86], [86, 94], [94, 102], [102, 110], [110, 118], [118, 126], [126, 134], [134, 142], [142, 150], [150, 158], [158, 166], [166, 174]]
const FILE_END = 174
const clipAfter = (segment) => CLIPS.find(([start]) => start === segment[1])
const CUT_EARLY = 0.12

let hasSeenHeroIntroVariable = false

export const hasSeenHeroIntro = () => hasSeenHeroIntroVariable

export const markHeroIntroSeen = () => {
    hasSeenHeroIntroVariable = true
}

export const checkPrefersReducedMotion = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const shuffled = (items, avoidFirst) => {
    const out = [...items]
    for (let i = out.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[out[i], out[j]] = [out[j], out[i]]
    }
    if (out.length > 1 && out[0] === avoidFirst) out.push(out.shift())
    return out
}

const Layer = styled.video`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 0;
    display: none;
    opacity: 0;

    html.hero-video & {
        display: block;
    }

    &.visible {
        opacity: 1;
    }
`

// The brand gradient laid over the greyscale footage turns it into a blue
// duotone and keeps the white headline readable.
const Tint = styled.div`
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(
        160deg,
        ${(props) => darken(0.06, adjustHue(-15, props.theme.palette.brand.blue))} 0%,
        ${(props) => props.theme.palette.brand.blue} 50%,
        ${(props) => lighten(0.04, adjustHue(8, props.theme.palette.brand.blue))} 100%
    );

    opacity: ${(props) => (props.settled ? 0.8 : props.playing ? 0.8 : 0.3)};
    transition: ${(props) => (props.settled ? 'none' : `opacity ${INTRO_SETTLE}s cubic-bezier(0.6, 0, 0.4, 1)`)};
`

// Low-res blurred placeholder (LQIP) of the first frame (~460 bytes)
// Inlined data URI paints on frame 0 with zero network latency.
const LOW_RES_POSTER =
    'data:image/jpeg;base64,/9j//gAQTGF2YzYyLjExLjEwMAD/2wBDAAgYGBwYHCEhISEhISckJygoKCcnJycoKCgrKyszMzMrKysoKCsrMDAzMzc5NzQ0MzQ5OTw8PEhIRUVUVFdnZ3z/xABpAAADAQEBAAAAAAAAAAAAAAAGBQQHAgMBAQEAAAAAAAAAAAAAAAAAAAABEAABAgUCBAQHAQAAAAAAAAABAhEDACExEkEiUXFhMoEEE0LxsvDBsVKRchEBAAAAAAAAAAAAAAAAAAAAAP/AABEIABsAMAMBIgACEQADEQD/2gAMAwEAAhEDEQA/AEBBAYkbjxUL2sJIvQUtLrAFCzFz8vSQ/NCXBNcmZ9H06zpZ2QgRud9fxrMREmDFCVEYYgubu7DpLfctI4E5A1avCe0KhRkArK4ZFNUvbm8qkRYZQtJcMkdpNy7/ABkIIkBS0qDpIe4UeZuLSHr8vESylFLjaWUbNyu0m8TFWQzSQe3E1YAUd7u/hI7GFy79tPC0hzDiRP2F6fbSSdMYpUhSlJDP7iHfoBWcrJIQRJelIViSHOIH8kNGhLiFKyFJLrcVoKClnlbE9RQIyYAgOFKclje3F3nzhjGEWpy8Ji8xWAmpvxI0PCQX+ZUvA7hYJFTcF/oyHMrIVeuqjQkvpL1YeC5qQAQdbiUkT2/6Eh//2Q=='

const BlurredPlaceholder = styled.div`
    position: absolute;
    inset: -24px;
    background-image: url('${LOW_RES_POSTER}');
    background-size: cover;
    background-position: center;
    filter: blur(20px);
    transform: scale(1.06);
    z-index: 0;
    pointer-events: none;
    display: none;
    opacity: ${(props) => (props.visible ? 1 : 0)};
    transition: opacity 0.5s ease;

    html.hero-video & {
        display: block;
    }
`

const HighResPoster = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 0;
    pointer-events: none;
    display: none;
    opacity: ${(props) => (props.visible ? 1 : 0)};
    transition: opacity 0.4s ease;

    html.hero-video & {
        display: block;
    }
`

const SpinnerContainer = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 4;
    pointer-events: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: ${(props) => (props.visible ? 1 : 0)};
    visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
    transition: opacity 0.35s ease, visibility 0.35s ease;
`

const spin = keyframes`
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
`

const SpinnerSvg = styled.svg`
    width: 44px;
    height: 44px;
    animation: ${spin} 0.85s linear infinite;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.25));

    circle.track {
        stroke: rgba(255, 255, 255, 0.22);
        stroke-width: 3.5;
        fill: none;
    }

    circle.arc {
        stroke: #ffffff;
        stroke-width: 3.5;
        stroke-linecap: round;
        stroke-dasharray: 85;
        stroke-dashoffset: 60;
        fill: none;
    }
`

export const useHeroReel = () => {
    const a = useRef(null)
    const b = useRef(null)
    const [alreadySeen, setAlreadySeen] = useState(hasSeenHeroIntro)
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(checkPrefersReducedMotion)
    const [playing, setPlaying] = useState(alreadySeen)

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
        const onChange = (e) => setPrefersReducedMotion(e.matches)
        if (mq.addEventListener) {
            mq.addEventListener('change', onChange)
            return () => mq.removeEventListener('change', onChange)
        } else if (mq.addListener) {
            mq.addListener(onChange)
            return () => mq.removeListener(onChange)
        }
        return undefined
    }, [])

    useEffect(() => {
        if (prefersReducedMotion) return undefined

        const players = [a.current, b.current]
        if (!players[0] || !players[1]) {
            return undefined
        }

        // Ensure hero-video class is present on root
        document.documentElement.classList.add('hero-video')

        const isReturning = hasSeenHeroIntro()
        setAlreadySeen(isReturning)

        let active = 0
        let queue = shuffled(CLIPS)
        let segment = isReturning ? queue.shift() : INTRO
        let next = queue.shift()
        let primed = false
        let ready = false
        let frame = 0
        let onScreen = true
        let tabVisible = !document.hidden
        let cleanupFallback = null

        const started = () => {
            setPlaying(true)
            markHeroIntroSeen()
        }

        const markReady = (event) => {
            if (event.target === players[1 - active] && event.target.readyState >= 2) {
                ready = true
            }
        }

        players.forEach((video) => {
            video.muted = true
            video.defaultMuted = true
            video.playsInline = true
            video.setAttribute('muted', '')
            video.setAttribute('playsinline', '')
            video.setAttribute('webkit-playsinline', '')
            video.preload = 'auto'
            video.src = heroReel
            video.addEventListener('seeked', markReady)
            video.addEventListener('canplay', markReady)
        })

        // Cut a few frames before each boundary to avoid overshoot flash
        const tick = () => {
            const current = players[active]
            const standby = players[1 - active]
            if (!current || !standby) return
            const t = current.currentTime

            if (!primed) {
                ready = false
                standby.currentTime = next[0]
                primed = true
            }

            if (t >= segment[1] - CUT_EARLY) {
                if (!ready && segment[1] < FILE_END) {
                    segment = clipAfter(segment)
                } else if (!ready) {
                    current.currentTime = next[0]
                    segment = next
                    if (!queue.length) queue = shuffled(CLIPS, segment)
                    next = queue.shift()
                    primed = false
                } else {
                    if (onScreen && tabVisible) standby.play().catch(() => {})
                    standby.classList.add('visible')
                    current.classList.remove('visible')
                    current.pause()
                    active = 1 - active
                    segment = next
                    if (!queue.length) queue = shuffled(CLIPS, segment)
                    next = queue.shift()
                    primed = false
                    ready = false
                }
            }

            frame = window.requestAnimationFrame(tick)
        }

        const resume = () => {
            const current = players[active]
            if (!current) return
            if (onScreen && tabVisible && current.currentTime < segment[1] - CUT_EARLY) {
                current.play().catch(() => {})
            } else {
                current.pause()
            }
        }

        const observer = new IntersectionObserver(([entry]) => {
            onScreen = entry.isIntersecting
            resume()
        })
        if (players[0].parentElement) {
            observer.observe(players[0].parentElement)
        }

        const onVisibility = () => {
            tabVisible = !document.hidden
            resume()
        }
        document.addEventListener('visibilitychange', onVisibility)

        if (isReturning) {
            // Returning to home: skip mosaic intro and jump straight to stretches
            const startStretchPlayback = () => {
                const player = players[active]
                player.classList.add('visible')
                player.play().catch(() => {})
                started()
            }

            const applyInitialSeek = () => {
                const player = players[active]
                player.addEventListener('seeked', startStretchPlayback, { once: true })
                player.currentTime = segment[0]
                cleanupFallback = window.setTimeout(startStretchPlayback, 250)
            }

            if (players[active].readyState >= 1) {
                applyInitialSeek()
            } else {
                players[active].addEventListener('loadedmetadata', applyInitialSeek, { once: true })
            }
        } else {
            // First time loading: play intro from 0s and notify once playing
            players[0].addEventListener('playing', started, { once: true })
            cleanupFallback = window.setTimeout(started, 2500)
            players[0].classList.add('visible')
            players[0].play().catch(() => {})
        }

        frame = window.requestAnimationFrame(tick)

        return () => {
            window.cancelAnimationFrame(frame)
            if (cleanupFallback) window.clearTimeout(cleanupFallback)
            observer.disconnect()
            document.removeEventListener('visibilitychange', onVisibility)
            players.forEach((video) => {
                video.removeEventListener('seeked', markReady)
                video.removeEventListener('canplay', markReady)
                video.pause()
            })
        }
    }, [prefersReducedMotion])

    return {
        reel: { a, b },
        playing,
        hasSeenIntro: alreadySeen,
        prefersReducedMotion
    }
}

export default function HeroVideo({ reel, playing, hasSeenIntro, prefersReducedMotion }) {
    const [posterLoaded, setPosterLoaded] = useState(false)
    const [showSpinner, setShowSpinner] = useState(false)

    useEffect(() => {
        if (hasSeenIntro || prefersReducedMotion) return undefined

        const img = new Image()
        img.src = heroPoster
        if (img.complete) {
            setPosterLoaded(true)
        } else {
            img.onload = () => setPosterLoaded(true)
        }
    }, [hasSeenIntro, prefersReducedMotion])

    useEffect(() => {
        if (hasSeenIntro || playing || prefersReducedMotion) {
            setShowSpinner(false)
            return undefined
        }

        const timer = window.setTimeout(() => {
            setShowSpinner(true)
        }, 1300)

        return () => window.clearTimeout(timer)
    }, [hasSeenIntro, playing, prefersReducedMotion])

    if (prefersReducedMotion) return null

    // Low-res blurred placeholder paints on frame 0 and holds until EITHER
    // the high-res poster loads OR the video starts playing
    const showPlaceholder = !hasSeenIntro && !playing && !posterLoaded
    // High-res poster is shown if it wins the race before the video starts playing
    const showHighRes = !hasSeenIntro && !playing && posterLoaded

    return (
        <>
            <BlurredPlaceholder visible={showPlaceholder} aria-hidden="true" />
            <HighResPoster
                src={heroPoster}
                fetchPriority="high"
                alt=""
                visible={showHighRes}
                aria-hidden="true"
            />
            <Layer
                ref={reel.a}
                muted
                playsInline
                preload="auto"
                aria-hidden="true"
            />
            <Layer
                ref={reel.b}
                muted
                playsInline
                preload="auto"
                aria-hidden="true"
            />
            <Tint settled={hasSeenIntro} playing={playing} />
            <SpinnerContainer visible={showSpinner && !playing}>
                <SpinnerSvg viewBox="0 0 50 50" aria-label="Loading">
                    <circle className="track" cx="25" cy="25" r="20" />
                    <circle className="arc" cx="25" cy="25" r="20" />
                </SpinnerSvg>
            </SpinnerContainer>
        </>
    )
}
